import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import trans from 'translate';
import PropTypes from 'prop-types';
import TokenWithNetwork from '../../Common/TokenWithNetwork/TokenWithNetwork';
import ChevronIcon from '../../Common/Icons/ChevronRightIcon';
import './EditTokensModal.scss';
import NavigationControls from './NavigationControls/NavigationControls';
import { usePromise } from '../../../hooks/usePromise/usePromise';
import { getBaseToken, updateHPool } from '../../../services/createHPoolService';
import configJson from '../../../config/config.json';
import { getValueByMultiProps } from '../../../utils/mapper';
import ShowAt from '../../Common/ShowAt/ShowAt';
import { updateTradeOrders } from '../../../redux/actions/createHPoolActions';
import { displayFloats } from '../../../utils/numbersDisplay';
import { getTokenSymbol } from '../../../utils/format';

const ConfirmTokensChanges = ({
  onNextStage,
  onPrevStage,
  hpool,
  baseTokens,
  modifiedModalTokens,
  setModifiedModalTokens,
  updateTradeOrders,
}) => {
  const baseCoinName = getTokenSymbol(getBaseToken(window.configTokens) || {});
  const getMultiProp = (token, key) => getValueByMultiProps(token, [`token_${key}`, key]);

  const mergeTradeOrdersWithAssets = (tradeOrder) => {
    const foundInAssets = modifiedModalTokens.find(
      (asset) => getMultiProp(asset, 'symbol')?.toLowerCase() === getMultiProp(tradeOrder, 'symbol')?.toLowerCase(),
    );
    return { ...foundInAssets, ...tradeOrder };
  };

  const handleSaveSuccess = ({ pending_market_trade_orders: pendingOrders = [] }) => {
    const tokensMergedWithTradeOrders = pendingOrders.map(mergeTradeOrdersWithAssets);
    const baseCoin = modifiedModalTokens?.find((order) => order?.token_symbol?.toLowerCase()?.includes(baseCoinName));
    setModifiedModalTokens([...tokensMergedWithTradeOrders, baseCoin]);
    updateTradeOrders(pendingOrders);
    onNextStage();
  };

  const [updateHpoolReq, , loading] = usePromise(updateHPool, { showError: true });

  const mapTokenAssets = (tokens = []) =>
    tokens.reduce((acc, curr) => {
      acc[curr.token_id || curr.id] = displayFloats(curr.percentage); // to make sure u only sending (two decimals as max)
      return acc;
    }, {});

  const onSave = () => {
    const { id } = hpool || {};
    const mergedWithUpdatedAssets = { hpool_id: id, id, assets: mapTokenAssets(modifiedModalTokens) };
    updateHpoolReq(mergedWithUpdatedAssets).then(handleSaveSuccess);
  };

  const findCurrentToken = (id) => baseTokens.find((token) => getMultiProp(token, 'id') === id) || {};

  const reformatModifiedTokens = (token) => {
    const { id: tokenId } = token;
    const prevToken = findCurrentToken(getMultiProp(token, 'id')) || {};
    const prevPercentage = displayFloats(getMultiProp(prevToken, 'percentage')) || 0;
    const percentage = displayFloats(getMultiProp(token, 'percentage')) || 0;
    const hasNoUpdate = prevPercentage === percentage;
    return { prevToken, prevPercentage, percentage, hasNoUpdate, tokenId, token };
  };

  const filterTokensWithNoUpdates = (token) => !token.hasNoUpdate;

  const rendredTokens = useMemo(
    () => modifiedModalTokens.map(reformatModifiedTokens).filter(filterTokensWithNoUpdates),
    [modifiedModalTokens],
  );

  return (
    <div className="tokens-outer-container">
      <p className="text text-center color-light m-0"> {trans('modal.confirm_token_changes_warning')}</p>
      <div className="tokens-container">
        {rendredTokens &&
          rendredTokens.map(({ token, prevToken, prevPercentage, percentage }) => (
            <div key={token.id} className="review-token-container">
              <div className="row">
                <div className="token-wrapper">
                  <TokenWithNetwork token={token} />
                  <div className="token-name">{getMultiProp(token, 'name')}</div>
                </div>
                <div className="token-status">
                  {!percentage && 'Removed'}
                  {!prevPercentage && 'Added'}
                  {!!prevPercentage && !!percentage && !!Math.abs(percentage - prevPercentage) && 'Updated'}
                </div>
              </div>
              <div className="row">
                <div className="token-percentage white-space-pre flex-end">
                  {!!Math.abs(percentage - prevPercentage) && (
                    <>
                      <span>{`${prevPercentage}%`}</span>
                      <span className="chevron-right-container">
                        <ChevronIcon />
                      </span>
                    </>
                  )}
                  <span>{`${percentage}%`}</span>
                </div>
              </div>
            </div>
          ))}
      </div>
      <NavigationControls
        loading={loading}
        nextTitle="button.save"
        laodingTitle="hpool.updating"
        onNext={onSave}
        onPrev={onPrevStage}
      />
    </div>
  );
};

const mapStateToProps = ({ createHPool }) => ({
  addedTokens: createHPool.addedTokens,
  hpool: createHPool.hpool,
});

const mapDispatchToProps = {
  updateTradeOrders,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConfirmTokensChanges);

ConfirmTokensChanges.defaultProps = {
  onNextStage: null,
  onPrevStage: null,
  setModifiedModalTokens: () => {},
};

ConfirmTokensChanges.propTypes = {
  onNextStage: PropTypes.func,
  onPrevStage: PropTypes.func,
  setModifiedModalTokens: PropTypes.func,
  hpool: PropTypes.object.isRequired,
  baseTokens: PropTypes.array.isRequired,
  modifiedModalTokens: PropTypes.array.isRequired,
  updateTradeOrders: PropTypes.func.isRequired,
};
